/* eslint-disable max-lines */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import i18n from 'i18n-react';
import { HashLink as Link } from 'react-router-hash-link';
import { Spring } from 'react-spring';
import { cssTransition, toast, ToastContainer } from 'react-toastify';

// import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import BackVideo from 'components/BackVideo';
import LanguageSelect from 'components/LanguageSelect';
import SearchInput from 'components/SearchModal/SearchInput';
import StoreButtons from 'components/StoreButtons';

import APIHandler from 'config/APIHandler';
import DataHandler from 'config/DataHandler';
import StorageHandler from 'config/StorageHandler';

import { useSettings } from 'hooks/useSettings';
import { useUserInfo } from 'hooks/useUserInfo';

import 'react-toastify/dist/ReactToastify.css';

import { bgVideo, storageKeys } from 'settings';

const Zoom = cssTransition({
  enter: 'zoomIn',
  exit: 'zoomOut',
  duration: [500, 800],
});

// renderable component for the toast. To be passed into the toast.info() function and defined here to prevent re-rendering.
const ToastContent = ({ message = '' }) => (
  <div>
    <p>
      <Link to="/main/rewards" key="toast">
        <span dangerouslySetInnerHTML={{ __html: message }} />
        <br />
        <br />
        {i18n.translate('homeScreen.readMore')}
      </Link>
    </p>
  </div>
);

/**
 * Landing page for the application.
 *
 * @returns {JSX.Element} HomeView component
 */
const HomeView = () => {
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [lastCheckedMessages, setLastCheckedMessages] = useState('1993-01-10');

  const { appId = -1, appInfo: { logoWhite } = {} } = useSettings();
  const { user = {} } = useUserInfo();

  // Gathers both the read messages and unread messages and stores them in states.
  const getUnreadMessages = useCallback(async () => {
    // First checks if the checked messages have been checked before, and if so, sets the lastCheckedMessages state to that value.
    let storedLastCheckedMessages = await StorageHandler.load(storageKeys.lastCheckedMessage);
    if (storedLastCheckedMessages) {
      setLastCheckedMessages(storedLastCheckedMessages);
    } else {
      storedLastCheckedMessages = '1993-01-10';
    }

    // TODO temp fix review, wasn't working based on delay of the state update
    // Next, checks for unread messages through the API --> this.checkForUnreadMessages()
    const unreadMessages = await DataHandler.requestWithId(APIHandler.getUnreadMessages, storedLastCheckedMessages);

    const { missed_items: missedItems } = unreadMessages;
    if (missedItems[0]) {
      setUnreadMessageCount(missedItems[0].count);
    }
  }, [lastCheckedMessages]);

  // If the salescompanion has not been registered yet for user (sales_companion_registered === null), this function will register the current date as the registration date.
  const registerSalesCompanion = useCallback(async () => {
    const { sales_companion_registered: salesCompanionRegistered } = user ?? {};

    if (user && salesCompanionRegistered === null) {
      const obj = user;
      const d = new Date(Date.now());
      const leading = (val) => `0${val}`.slice(-2);
      const date = `${d.getFullYear()}-${leading(d.getMonth() + 1)}-${leading(d.getDate())}`;

      const res = await APIHandler.updateUser(appId, obj.email, [{ param: 'sales_companion_registered', val: date }]);

      if (res) {
        obj.sales_companion_registered = date;
        StorageHandler.save(storageKeys.user, obj);
      }
    }
  }, [appId, user]);

  // Checks for announcements in the API and stores them in local storage.
  const setAnnouncements = useCallback(async () => {
    StorageHandler.load(storageKeys.lang).then(async (langCode) => {
      const res = await APIHandler.getContests(appId, {
        currentDate: new Date().toISOString(),
        marketFilter: [{ id: langCode.code.toUpperCase() }],
      });
      const locale = `${langCode.langFile.toLowerCase()}-${langCode.code.toUpperCase()}`;

      // If there are messages returned, register the formatted information to the toast.
      if (res.length !== 0 && res && res[0].title) {
        const { start_date: startDate, end_date: endDate, summary } = res[0];

        // Convert to month / day format
        const message = `<b>${summary}</b> <br /><br /> ${new Date(startDate).toLocaleDateString(locale, {
          month: 'long',
          day: 'numeric',
        })} - ${new Date(endDate).toLocaleDateString(locale, { day: 'numeric', month: 'long' })}`;

        if(appId !== -1){
          toast.info(() => ToastContent({ message }), {
            position: 'top-center',
            autoClose: false,
            closeOnClick: false,
            transition: Zoom,
            hideProgressBar: true,
            pauseOnHover: true,
            draggable: true,
            toastId: 'announcement',
          });
        }
      }
    });
  }, [appId]);

  useEffect(() => {
    document.body.classList.add('no-scroll');
    getUnreadMessages();

    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [getUnreadMessages]);

  useEffect(() => {
    registerSalesCompanion();
  }, [registerSalesCompanion]);

  useEffect(() => {
    setAnnouncements();
  }, [setAnnouncements]);

  return (
    <div className="homeContainer">
      {bgVideo && <BackVideo />}
      <div className="homeView__buttonWrapper--download-app">
        {i18n.translate('homeScreen.downloadNow')}
        <StoreButtons />
      </div>
      <LanguageSelect />
      <Spring from={{ transform: 'translateY(50%)', opacity: 0 }} to={{ transform: 'translateY(0%)', opacity: 1 }}>
        {(props) => (
          <div className="homeView" style={props}>
            <div className="homeView__title">
              <img src={logoWhite} className="homeLogo" alt="logo" />
              <div className="homeView__title--action">
                <SearchInput />
                <div className="homepage__buttons homepage__buttons--main">
                  <Link to="/main/messages" className="startTrain startTrain--blue">
                    <div className="startTrain__icon-container">
                      <FontAwesomeIcon icon={['fas', 'envelope']} />
                      {unreadMessageCount > 0 && (
                        <FontAwesomeIcon
                          className="startTrain__warning-icon--overlapping"
                          icon={['fas', 'exclamation-circle']}
                        />
                      )}
                    </div>
                    {<p>{i18n.translate('nav.messages')}</p>}
                  </Link>
                  <Link to="/main/rewards" className="startTrain startTrain--red">
                    <FontAwesomeIcon icon={['fas', 'treasure-chest']} />
                    <p>{i18n.translate('nav.rewards')}</p>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </Spring>
      <div className="top-bar" />
      <div className="bottom-bar" />
      {/* <SweetAlert
          title="This feature is Coming Soon"
          show={noAgent}
          imageUrl={Logo2}
          imageWidth="50rem"
          imageHeight="50rem"
          html
          text=""
          customClass="noAgent"
          showConfirmButton={false}
          cancelButtonText={i18n.translate('general.close')}
          showCancelButton
          onConfirm={close}
          onCancel={close}
          onEscapeKey={close}
          onOutsideClick={close}
        /> */}
      <ToastContainer
        draggablePercent={60}
        autoClose={false}
        newestOnTop
        rtl={false}
        pauseOnVisibilityChange
        draggable
      />
    </div>
  );
};

export default HomeView;

ToastContent.propTypes = {
  message: PropTypes.string,
};
